import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { AxiosError } from 'axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import {
  createConnector,
  createVersion,
  getApiErrorMessage,
  previewVersion,
  publishVersion,
  updateConnector,
  updateVersion,
} from './connectorsApi';

export const useCreateConnectorHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createConnector,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      dispatch(showSnackbar({ message: 'Connector created', severity: 'success' }));
      onSuccess?.(data);
    },
    onError: (error) => {
      dispatch(
        showSnackbar({
          message: getApiErrorMessage(error),
          severity: 'error',
        }),
      );
    },
  });

  return { mutate: mutation.mutate, mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
};

export const useUpdateConnectorHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ connectorKey, payload }) => updateConnector(connectorKey, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorKey] });
      dispatch(showSnackbar({ message: 'Connector updated', severity: 'success' }));
      onSuccess?.(data);
    },
    onError: (error) => {
      dispatch(showSnackbar({ message: getApiErrorMessage(error), severity: 'error' }));
    },
  });

  return { mutate: mutation.mutate, isPending: mutation.isPending };
};

export const useCreateVersionHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ connectorKey, payload }) => createVersion(connectorKey, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connector-versions', variables.connectorKey] });
      dispatch(showSnackbar({ message: 'Version created', severity: 'success' }));
      onSuccess?.(data, variables);
    },
    onError: (error) => {
      dispatch(showSnackbar({ message: getApiErrorMessage(error), severity: 'error' }));
    },
  });

  return { mutate: mutation.mutate, mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
};

export const useUpdateVersionHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ connectorKey, versionNumber, payload }) => updateVersion(connectorKey, versionNumber, payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connector-versions', variables.connectorKey] });
      queryClient.invalidateQueries({
        queryKey: ['connector-version', variables.connectorKey, variables.versionNumber],
      });
      dispatch(showSnackbar({ message: 'Draft saved', severity: 'success' }));
      onSuccess?.(data);
    },
    onError: (error) => {
      const missing = error instanceof AxiosError ? error.response?.data?.missing : null;
      dispatch(
        showSnackbar({
          message: getApiErrorMessage(error),
          severity: 'error',
          ...(missing ? { details: missing } : {}),
        }),
      );
    },
  });

  return { mutate: mutation.mutate, mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
};

export const usePreviewVersionHandler = () => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: ({ connectorKey, versionNumber, payload }) => previewVersion(connectorKey, versionNumber, payload),
    onError: (error) => {
      dispatch(showSnackbar({ message: getApiErrorMessage(error), severity: 'error' }));
    },
  });

  return {
    preview: mutation.mutateAsync,
    previewResult: mutation.data,
    isPreviewing: mutation.isPending,
    resetPreview: mutation.reset,
  };
};

export const usePublishVersionHandler = ({ onSuccess } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ connectorKey, versionNumber }) => publishVersion(connectorKey, versionNumber),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connectors'] });
      queryClient.invalidateQueries({ queryKey: ['connector', variables.connectorKey] });
      queryClient.invalidateQueries({ queryKey: ['connector-versions', variables.connectorKey] });
      queryClient.invalidateQueries({
        queryKey: ['connector-version', variables.connectorKey, variables.versionNumber],
      });
      dispatch(showSnackbar({ message: 'Version published', severity: 'success' }));
      onSuccess?.(data);
    },
    onError: (error) => {
      dispatch(showSnackbar({ message: getApiErrorMessage(error), severity: 'error' }));
    },
  });

  return { mutate: mutation.mutate, mutateAsync: mutation.mutateAsync, isPending: mutation.isPending };
};
